import { useEffect, useRef, type MouseEvent } from "react";

/**
 * Close a modal on Escape or on a click outside it. Spread the result on the
 * BACKDROP element (the full-screen overlay), not on the panel.
 *
 * "Outside" means the press STARTED and ENDED on the backdrop. Checking only
 * the click target closes the dialog when someone selects text in a caption and
 * lets go past the panel's edge — the click lands on the backdrop although
 * nobody clicked it, and an edit in progress is gone.
 */
export function useDismiss(onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) close.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return {
    onMouseDown: (e: MouseEvent<HTMLElement>) => {
      pressedBackdrop.current = e.target === e.currentTarget;
    },
    onClick: (e: MouseEvent<HTMLElement>) => {
      if (pressedBackdrop.current && e.target === e.currentTarget) close.current();
      pressedBackdrop.current = false;
    },
  };
}
